import React, { useEffect, useState, useMemo } from 'react';
import { Card, Row, Col, Table, Badge, ProgressBar, Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom';
import * as moment from 'moment';
import DatePicker from '@app/components/CustomDatePicker';
import { toast } from 'react-toastify';
import SmallBox from '../components/small-box/SmallBox';
import { accountPagingAction } from '../store/reducers/account';
import { Post, Get } from '../utils/httpProvider';
import { MEMBER_TRACKING_API, POSITION_API, ACCOUNT_POSITION_API } from '../utils/apiUrl.js';
import { useQuery } from 'react-query';
import Pagination from 'react-js-pagination';
import { userManager } from '../utils/oidc';
import Select from 'react-select';

import Plotly from 'plotly.js-basic-dist';
import createPlotlyComponent from 'react-plotly.js/factory';
import locale from 'plotly.js-locales/vi';
const Plot = createPlotlyComponent(Plotly);
Plotly.register(locale);
Plotly.setPlotConfig({ locale: 'vi' });

const Dashboard = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const { listAccount } = useSelector((state) => state.account);
    const [trackingList, setTrackingList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [topFilter, setTopFilter] = useState('all');
    const [positionFilter, setPositionFilter] = useState('all');
    const [employeePositionFilter, setEmployeePositionFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentUser, setCurrentUser] = useState(null);

    const currentYear = new Date().getFullYear();
    const defaultFrom = `${currentYear}-01-01`;
    const defaultTo = `${currentYear}-12-31`;
    const [filterFrom, setFilterFrom] = useState(defaultFrom);
    const [filterTo, setFilterTo] = useState(defaultTo);
    const [appliedFrom, setAppliedFrom] = useState(defaultFrom);
    const [appliedTo, setAppliedTo] = useState(defaultTo);

    const itemsPerPage = 10;
    const EMPLOYEE_CHART_VALUE = 'employee';

    const { data: positionsData } = useQuery(['getAllActivePositionsDashboard'], async () => {
        const res = await Get(POSITION_API.GET_ALL_ACTIVE, null, null);
        return Array.isArray(res) ? res : (res?.data || []);
    });

    const positionOptions = useMemo(() => {
        if (!Array.isArray(positionsData)) {
            return [];
        }

        return positionsData
            .map(pos => ({
                value: pos.id || pos.Id,
                label: pos.name || pos.Name
            }))
            .filter(option => option.value);
    }, [positionsData]);

    const chartModeOptions = useMemo(() => ([
        { value: 'all', label: 'Tất cả vị trí' },
        ...positionOptions,
        { value: EMPLOYEE_CHART_VALUE, label: 'Tên nhân viên' }
    ]), [positionOptions, EMPLOYEE_CHART_VALUE]);

    const topFilterOptions = useMemo(() => ([
        { value: 'all', label: 'Tất cả nhân viên' },
        { value: '5', label: 'Top 5' },
        { value: '10', label: 'Top 10' },
        { value: '20', label: 'Top 20' }
    ]), []);

    const employeePositionOptions = useMemo(() => ([
        { value: 'all', label: 'Tất cả vị trí' },
        ...positionOptions
    ]), [positionOptions]);

    useEffect(() => {
        userManager.getUser().then(user => {
            setCurrentUser(user);
        });
    }, []);

    const isAdmin = useMemo(() => {
        const roles = currentUser?.profile?.role;
        if (!roles) return false;
        const normalizedRoles = Array.isArray(roles) ? roles : [roles];
        return normalizedRoles.some(role => String(role).toLowerCase() === 'admin');
    }, [currentUser]);

    const [positionsMap, setPositionsMap] = useState({});

    useEffect(() => {
        const fetchPositions = async () => {
            const rawList = isAdmin ? listAccount : (currentUser?.profile?.sub ? [{ id: currentUser.profile.sub }] : []);
            if (rawList && rawList.length > 0) {
                const ids = rawList.map(a => a.id || a.Id);
                try {
                    const response = await Post(ACCOUNT_POSITION_API.GET_BY_ACCOUNT_IDS, null, ids);
                    const res = await response.json();
                    const posList = Array.isArray(res) ? res : (res?.data || []);
                    const map = {};
                    posList.forEach(pos => {
                        map[pos.accountId] = pos;
                    });
                    setPositionsMap(map);
                } catch (error) {
                    console.error('Error fetching positions', error);
                }
            } else {
                setPositionsMap({});
            }
        };
        fetchPositions();
    }, [isAdmin, listAccount, currentUser]);

    useEffect(() => {
        if (isAdmin) {
            dispatch(accountPagingAction({ pageIndex: 1, pageSize: 1000, roleName: 'MEMBER_KPI_C9' }));
        }
    }, [dispatch, isAdmin]);

    const accounts = useMemo(() => {
        if (!isAdmin) {
            const currentUserId = currentUser?.profile?.sub;
            const currentUsername = currentUser?.profile?.preferred_username || currentUser?.profile?.name || '';

            if (!currentUserId) return [];

            const posId = positionsMap[currentUserId]?.positionId || null;
            const posName = positionsMap[currentUserId]?.positionName || '';
            const posCode = positionsMap[currentUserId]?.positionCode || '';

            return [{
                id: currentUserId,
                fullName: currentUsername || 'Tai khoan hien tai',
                userName: currentUsername,
                positionId: posId,
                positionName: posName,
                positionCode: posCode
            }];
        }

        return (listAccount || [])
            .filter(item => {
                const roles = item.roles || item.Roles || [];
                return roles.some(r => (r.name || r.Name) === 'MEMBER_KPI_C9');
            })
            .map(item => {
                const id = item.id || item.Id;
                const username = item.username || item.Username;
                const authInfo = item.accountAuthInfo || item.AccountAuthInfo;
                const lastName = authInfo?.lastName || authInfo?.LastName || '';
                const middleName = authInfo?.middleName || authInfo?.MiddleName || '';
                const firstName = authInfo?.firstName || authInfo?.FirstName || '';
                const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');

                const webPosition = positionsMap[id];
                const posId = webPosition?.positionId || authInfo?.positionId || authInfo?.PositionId || null;
                const posName = webPosition?.positionName || authInfo?.positionName || authInfo?.PositionName || '';
                const posCode = webPosition?.positionCode || authInfo?.positionCode || authInfo?.PositionCode || '';

                return {
                    id: id,
                    fullName: fullName || username,
                    userName: username,
                    positionId: posId,
                    positionName: posName,
                    positionCode: posCode
                };
            });
    }, [currentUser, isAdmin, listAccount, positionsMap]);

    useEffect(() => {
        if (accounts.length > 0) {
            fetchAllMembersTracking();
        } else {
            setTrackingList([]);
        }
    }, [accounts]);

    const fetchAllMembersTracking = async (overrideFrom, overrideTo) => {
        setLoading(true);
        try {
            const memberIds = accounts.map(a => a.id);
            const fromDateVal = overrideFrom !== undefined ? overrideFrom : filterFrom;
            const toDateVal = overrideTo !== undefined ? overrideTo : filterTo;
            const payload = {
                memberIds: memberIds,
                fromDate: fromDateVal || null,
                toDate: toDateVal || null
            };
            const response = await Post(MEMBER_TRACKING_API.GET_ALL_MEMBERS_TRACKING, null, payload);
            if (response && response.ok) {
                const data = await response.json();
                setAppliedFrom(fromDateVal);
                setAppliedTo(toDateVal);
                setTrackingList(data ?? []);
            }
        } catch (error) {
            console.error('Error fetching all members tracking:', error);
            setTrackingList([]);
        } finally {
            setLoading(false);
        }
    };

    const processedData = useMemo(() => {
        if (!trackingList || trackingList.length === 0) return [];

        return accounts.map(acc => {
            const memberData = trackingList.find(t => String(t.userId || t.UserId) === String(acc.id));

            let totalAnnualScore = 0;
            let totalRegisteredScore = 0;
            let totalUnassessedScore = 0;
            let progressPercentage = 0;
            let hasActiveAnnualJob = false;

            if (memberData) {
                const activeRegsObj = memberData.activeAnnualJobRegistrations ?? memberData.ActiveAnnualJobRegistrations;
                const latestRegObj = memberData.latestAnnualJobRegistration ?? memberData.LatestAnnualJobRegistration;

                let regList = [];
                if (activeRegsObj) {
                    regList = Array.isArray(activeRegsObj) ? activeRegsObj : (activeRegsObj.$values || []);
                } else if (latestRegObj) {
                    regList = [latestRegObj];
                }

                if (regList && regList.length > 0) {
                    hasActiveAnnualJob = true;

                    regList.forEach(regObj => {
                        const rawDetails = regObj.details ?? regObj.Details;
                        const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];

                        details.forEach(d => {
                            // Check valid date for detail
                            const status = d.status ?? d.Status ?? 0;
                            const dEndDate = d.endDate ?? d.EndDate;
                            const regStartDate = moment(regObj.startDate ?? regObj.StartDate);
                            const regEndDate = moment(regObj.endDate ?? regObj.EndDate);
                            const effectiveEndDate = dEndDate ? moment(dEndDate) : regEndDate;

                            let isValid = true;
                            if (appliedFrom && appliedTo) {
                                const fDate = moment(appliedFrom).startOf('day');
                                const tDateExclusive = moment(appliedTo).add(1, 'day').startOf('day');
                                if (status === 3) {
                                    const actualEndDate = d.actualEndDate ?? d.ActualEndDate;
                                    if (actualEndDate) {
                                        const actual = moment(actualEndDate);
                                        isValid = actual.isSameOrAfter(fDate) && actual.isBefore(tDateExclusive);
                                    } else {
                                        isValid = false;
                                    }
                                } else {
                                    isValid = regStartDate.isBefore(tDateExclusive) && effectiveEndDate.isSameOrAfter(fDate);
                                }
                            }

                            if (isValid && (status === 3 || status === 4)) {
                                const baseScore = d.currentScore ?? d.CurrentScore ?? 0;
                                const evaluationPercent = d.evaluationPercent ?? d.EvaluationPercent;
                                if (evaluationPercent !== null && evaluationPercent !== undefined) {
                                    totalAnnualScore += baseScore * Number(evaluationPercent) / 100;
                                } else {
                                    totalUnassessedScore += baseScore;
                                }
                                totalRegisteredScore += d.registeredScore ?? d.RegisteredScore ?? 0;
                            }
                        });
                    });

                    if (totalRegisteredScore > 0) {
                        progressPercentage = (totalAnnualScore / totalRegisteredScore) * 100;
                    }
                }
            }

            let totalProjectScore = 0;
            if (memberData) {
                const pList = memberData.projects ?? memberData.Projects ?? [];
                const rawProjList = Array.isArray(pList) ? pList : (pList.$values || []);

                rawProjList.forEach(p => {
                    const rawMembers = p.members ?? p.Members ?? [];
                    const members = Array.isArray(rawMembers) ? rawMembers : (rawMembers.$values || []);
                    const me = members.find(m => String(m.userId || m.UserId) === String(acc.id));
                    if (me) {
                        const statusVal = me.status ?? me.Status ?? 0;
                        if (statusVal === 3 || statusVal === 4) {
                            const baseScore = me.memberWeight ?? me.MemberWeight ?? 0;
                            const evaluationPercent = me.evaluationPercent ?? me.EvaluationPercent;
                            if (evaluationPercent !== null && evaluationPercent !== undefined) {
                                totalProjectScore += baseScore * Number(evaluationPercent) / 100;
                            } else {
                                totalUnassessedScore += baseScore;
                            }
                        }
                    }
                });
            }

            return {
                ...acc,
                totalAnnualScore,
                totalRegisteredScore,
                progressPercentage,
                totalProjectScore,
                totalScore: totalAnnualScore + totalProjectScore,
                totalUnassessedScore,
                projectedScore: totalAnnualScore + totalProjectScore + totalUnassessedScore,
                hasActiveAnnualJob
            };
        }).sort((a, b) => b.totalScore - a.totalScore);
    }, [accounts, trackingList, appliedFrom, appliedTo]);

    // Card Stats
    const totalMembers = accounts.length;

    const kpiCounts = useMemo(() => {
        let inProgress = 0;
        let completed = 0;
        const fDate = appliedFrom ? moment(appliedFrom).startOf('day') : null;
        const tDateExclusive = appliedTo ? moment(appliedTo).add(1, 'day').startOf('day') : null;

        trackingList.forEach(item => {
            const activeRegsObj = item.activeAnnualJobRegistrations ?? item.ActiveAnnualJobRegistrations;
            let regList = [];
            if (activeRegsObj) {
                regList = Array.isArray(activeRegsObj) ? activeRegsObj : (activeRegsObj.$values || []);
            }

            regList.forEach(regObj => {
                const rawDetails = regObj.details ?? regObj.Details;
                const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];
                const regStartDate = moment(regObj.startDate ?? regObj.StartDate);
                const regEndDate = moment(regObj.endDate ?? regObj.EndDate);

                details.forEach(d => {
                    const status = d.status ?? d.Status ?? 0;

                    if (fDate && tDateExclusive) {
                        // Đang thực hiện
                        if (status === 1) {
                            const dEndDate = d.endDate ?? d.EndDate;
                            const effectiveEndDate = dEndDate ? moment(dEndDate) : regEndDate;
                            if (regStartDate.isBefore(tDateExclusive) && effectiveEndDate.isSameOrAfter(fDate)) {
                                inProgress++;
                            }
                        }
                        // Hoàn thành (3)
                        if (status === 3) {
                            const actualEndDate = d.actualEndDate ?? d.ActualEndDate;
                            if (actualEndDate) {
                                const actual = moment(actualEndDate);
                                if (actual.isSameOrAfter(fDate) && actual.isBefore(tDateExclusive)) {
                                    completed++;
                                }
                            }
                        }
                    } else {
                        // Default if no filter
                        if (status === 1) inProgress++;
                        if (status === 3) completed++;
                    }
                });
            });
        });
        return { inProgress, completed };
    }, [trackingList, appliedFrom, appliedTo]);

    const activeAnnualJobs = kpiCounts.inProgress;

    const activeProjectsCount = useMemo(() => {
        const activeProjectIds = new Set();
        trackingList.forEach(item => {
            const pList = item.projects ?? item.Projects ?? [];
            const rawProjList = Array.isArray(pList) ? pList : (pList.$values || []);
            rawProjList.forEach(p => {
                const pStatus = p.status ?? p.Status ?? 0;
                if (pStatus === 0) {
                    activeProjectIds.add(p.id ?? p.Id);
                }
            });
        });
        return activeProjectIds.size;
    }, [trackingList]);

    const completedKpiCount = kpiCounts.completed;

    // Pie Chart Data - Annual Jobs Status distribution
    const annualStatusCounts = useMemo(() => {
        let todo = 0, inProgress = 0, waiting = 0, done = 0, overdue = 0;
        const fDate = appliedFrom ? moment(appliedFrom).startOf('day') : null;
        const tDateExclusive = appliedTo ? moment(appliedTo).add(1, 'day').startOf('day') : null;

        trackingList.forEach(item => {
            const activeRegsObj = item.activeAnnualJobRegistrations ?? item.ActiveAnnualJobRegistrations;
            let regList = [];
            if (activeRegsObj) {
                regList = Array.isArray(activeRegsObj) ? activeRegsObj : (activeRegsObj.$values || []);
            }

            regList.forEach(reg => {
                const regStartDate = moment(reg.startDate ?? reg.StartDate);
                const regEndDate = moment(reg.endDate ?? reg.EndDate);

                const rawDetails = reg.details ?? reg.Details;
                const details = (rawDetails && rawDetails.$values ? rawDetails.$values : rawDetails) || [];

                details.forEach(d => {
                    const status = d.status ?? d.Status ?? 0;
                    const dEndDate = d.endDate ?? d.EndDate;
                    const effectiveEndDate = dEndDate ? moment(dEndDate) : regEndDate;

                    let isValid = true;
                    if (fDate && tDateExclusive) {
                        if (status === 3) {
                            const actualEndDate = d.actualEndDate ?? d.ActualEndDate;
                            if (actualEndDate) {
                                const actual = moment(actualEndDate);
                                isValid = actual.isSameOrAfter(fDate) && actual.isBefore(tDateExclusive);
                            } else {
                                isValid = false;
                            }
                        } else {
                            isValid = regStartDate.isBefore(tDateExclusive) && effectiveEndDate.isSameOrAfter(fDate);
                        }
                    }

                    if (isValid) {
                        if (status === 0) todo++;
                        else if (status === 1) inProgress++;
                        else if (status === 2) waiting++;
                        else if (status === 3) done++;
                        else if (status === 4) overdue++;
                    }
                });
            });
        });

        return { todo, inProgress, waiting, done, overdue };
    }, [trackingList, appliedFrom, appliedTo]);

    // Pie Chart Data - Projects Status distribution
    const projectStatusCounts = useMemo(() => {
        const uniqueProjectsMap = new Map();
        trackingList.forEach(item => {
            const pList = item.projects ?? item.Projects ?? [];
            const rawProjList = Array.isArray(pList) ? pList : (pList.$values || []);
            rawProjList.forEach(p => {
                const pId = p.id ?? p.Id;
                if (!uniqueProjectsMap.has(pId)) {
                    uniqueProjectsMap.set(pId, p);
                }
            });
        });

        let inProgress = 0;
        let done = 0;
        uniqueProjectsMap.forEach(p => {
            const status = p.status ?? p.Status ?? 0;
            if (status === 1 || status === 2) {
                done++;
            } else {
                inProgress++;
            }
        });

        return { inProgress, done };
    }, [trackingList]);

    const isEmployeeChart = positionFilter === EMPLOYEE_CHART_VALUE;

    const positionChartData = useMemo(() => {
        const positionMap = new Map();

        if (Array.isArray(positionsData)) {
            positionsData.forEach(pos => {
                const id = pos.id || pos.Id;
                const name = pos.name || pos.Name;
                if (!id) {
                    return;
                }

                positionMap.set(String(id), {
                    id: String(id),
                    fullName: name || 'Chưa xác định',
                    positionName: name || 'Chưa xác định',
                    totalAnnualScore: 0,
                    totalProjectScore: 0,
                    totalScore: 0,
                    memberCount: 0
                });
            });
        }

        processedData.forEach(item => {
            const positionKey = item.positionId ? String(item.positionId) : 'unknown';
            const positionName = item.positionName || 'Chưa xác định';

            if (!positionMap.has(positionKey)) {
                positionMap.set(positionKey, {
                    id: positionKey,
                    fullName: positionName,
                    positionName,
                    totalAnnualScore: 0,
                    totalProjectScore: 0,
                    totalScore: 0,
                    memberCount: 0
                });
            }

            const group = positionMap.get(positionKey);
            group.totalAnnualScore += item.totalAnnualScore;
            group.totalProjectScore += item.totalProjectScore;
            group.totalScore += item.totalScore;
            group.memberCount += 1;
        });

        const result = Array.from(positionMap.values()).sort((a, b) => b.totalScore - a.totalScore);

        if (positionFilter === 'all') {
            return result;
        }

        return result.filter(item => String(item.id) === String(positionFilter));
    }, [processedData, positionFilter, positionsData]);

    const employeeChartData = useMemo(() => {
        const filteredByPosition = employeePositionFilter === 'all'
            ? processedData
            : processedData.filter(item => String(item.positionId) === String(employeePositionFilter));

        return topFilter === 'all' ? filteredByPosition : filteredByPosition.slice(0, parseInt(topFilter, 10));
    }, [processedData, topFilter, employeePositionFilter]);

    const visibleChartData = isEmployeeChart ? employeeChartData : positionChartData;

    // Traces for Stacked Bar Chart
    const barChartData = useMemo(() => {
        const names = visibleChartData.map(d => d.fullName);
        const annualScores = visibleChartData.map(d => d.totalAnnualScore);
        const projectScores = visibleChartData.map(d => d.totalProjectScore);

        return [
            {
                x: names,
                y: annualScores,
                name: 'Thường niên',
                type: 'bar',
                marker: { color: '#2da44e' } // Green
            },
            {
                x: names,
                y: projectScores,
                name: 'Đột xuất / Khoán',
                type: 'bar',
                marker: { color: '#0969da' } // Blue
            }
        ];
    }, [visibleChartData]);

    const barChartLayout = {
        barmode: 'stack',
        xaxis: {
            title: isEmployeeChart ? 'Họ tên nhân viên' : 'Vị trí',
            tickangle: -20
        },
        yaxis: {
            title: 'Điểm KPI tích lũy'
        },
        margin: { l: 50, r: 30, t: 30, b: 60 },
        height: 380,
        autosize: true
    };

    // Traces for Pie Charts
    const annualPieChartData = useMemo(() => {
        const labels = ['Cần làm', 'Đang làm', 'Hoàn thành', 'Quá hạn'];
        const values = [
            annualStatusCounts.todo,
            annualStatusCounts.inProgress,
            annualStatusCounts.done,
            annualStatusCounts.overdue
        ];

        return [{
            values,
            labels,
            type: 'pie',
            hole: 0.45,
            marker: {
                colors: ['#6c757d', '#007bff', '#28a745', '#dc3545']
            },
            textinfo: 'value',
            textposition: 'inside',
            automargin: true
        }];
    }, [annualStatusCounts]);

    const annualPieLayout = {
        height: 260,
        margin: { l: 10, r: 10, t: 10, b: 10 },
        showlegend: true,
        legend: { orientation: 'h', y: -0.1 }
    };

    const projectPieChartData = useMemo(() => {
        const labels = ['Đang thực hiện', 'Hoàn thành'];
        const values = [
            projectStatusCounts.inProgress,
            projectStatusCounts.done
        ];

        return [{
            values,
            labels,
            type: 'pie',
            hole: 0.45,
            marker: {
                colors: ['#ffc107', '#28a745']
            },
            textinfo: 'value',
            textposition: 'inside',
            automargin: true
        }];
    }, [projectStatusCounts]);

    const projectPieLayout = {
        height: 260,
        margin: { l: 10, r: 10, t: 10, b: 10 },
        showlegend: true,
        legend: { orientation: 'h', y: -0.1 }
    };

    const personalKpi = processedData.length > 0 ? processedData[0] : null;

    if (currentUser && !isAdmin) {
        return <Redirect to="/annual-job/tracking" />;
    }

    return (
        <div className="container-fluid">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-4 border-bottom pb-3">
                <div className="mb-3 mb-lg-0">
                    <h2 className="mb-1 text-dark fw-bold" style={{ fontSize: '24px' }}>Tổng quan KPI (Phân hệ 3)</h2>
                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Giám sát trực quan hiệu suất công việc và điểm số KPI của toàn bộ nhân viên</p>
                </div>
                <div className="d-flex flex-wrap align-items-center">
                    <div className="text-muted mr-2 mb-2" style={{ fontSize: '13px' }}>Từ ngày:</div>
                    <div style={{ width: '130px' }} className="mr-3 mb-2">
                        <DatePicker 
                            className="form-control form-control-sm w-100"
                            selected={filterFrom ? new Date(filterFrom) : null}
                            onChange={(date) => setFilterFrom(date ? moment(date).format('YYYY-MM-DD') : '')}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/MM/yyyy"
                            isClearable
                        />
                    </div>
                    <div className="text-muted mr-2 mb-2" style={{ fontSize: '13px' }}>Đến ngày:</div>
                    <div style={{ width: '130px' }} className="mr-3 mb-2">
                        <DatePicker 
                            className="form-control form-control-sm w-100"
                            selected={filterTo ? new Date(filterTo) : null}
                            onChange={(date) => setFilterTo(date ? moment(date).format('YYYY-MM-DD') : '')}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/MM/yyyy"
                            isClearable
                        />
                    </div>
                    <Button variant="primary" onClick={() => {
                        if (!filterFrom || !filterTo) {
                            toast.warning('Vui lòng chọn đầy đủ Từ ngày và Đến ngày');
                            return;
                        }
                        if (filterFrom > filterTo) {
                            toast.warning('Từ ngày không được lớn hơn Đến ngày');
                            return;
                        }
                        fetchAllMembersTracking();
                    }} disabled={loading} size="sm" className="mr-2 mb-2">
                        <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-filter'} mr-1`}></i> Lọc
                    </Button>
                    <Button variant="outline-secondary" onClick={() => {
                        setFilterFrom(defaultFrom);
                        setFilterTo(defaultTo);
                        fetchAllMembersTracking(defaultFrom, defaultTo);
                    }} disabled={loading} size="sm" className="mb-2">
                        <i className="fas fa-times mr-1"></i> Xóa lọc
                    </Button>
                </div>
            </div>

            {loading && trackingList.length === 0 ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status"></div>
                    <div className="mt-3 text-muted">Đang tải dữ liệu tổng quan...</div>
                </div>
            ) : (
                <>
                    {/* Section 1: Summary Cards */}
                    <Row className="mb-4">
                        <Col lg={3} sm={6} className="mb-3">
                            <SmallBox type="info" count={totalMembers} title="Tổng nhân sự" des="Nhân sự MEMBER_KPI_C9" />
                        </Col>
                        <Col lg={3} sm={6} className="mb-3">
                            <SmallBox type="success" count={activeAnnualJobs} title="Tổng KPI thường niên" des="Tổng công việc đang tiến hành" />
                        </Col>
                        <Col lg={3} sm={6} className="mb-3">
                            <SmallBox type="warning" count={activeProjectsCount} title="Công việc đột xuất" des="Tổng công việc đột xuất đang tiến hành" />
                        </Col>
                        <Col lg={3} sm={6} className="mb-3">
                            <SmallBox type="primary" count={completedKpiCount} title="KPI đã hoàn thành" des="Công việc KPI đã hoàn thành" />
                        </Col>
                    </Row>

                    {/* Section 2: Bar Chart */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="shadow-sm">
                                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                    <h3 className="card-title fw-bold" style={{ fontSize: '16px', margin: 0 }}>
                                        <i className="fas fa-chart-bar text-success me-2"></i>
                                        Biểu đồ liên kết điểm KPI theo nhân sự
                                    </h3>
                                    <div
                                        className="d-flex align-items-center"
                                        style={{ width: '720px', maxWidth: '100%', justifyContent: 'flex-start' }}
                                    >
                                        <Select
                                            className="mr-2"
                                            value={chartModeOptions.find(option => String(option.value) === String(positionFilter)) || chartModeOptions[0]}
                                            onChange={(option) => setPositionFilter(option?.value || 'all')}
                                            options={chartModeOptions}
                                            isSearchable
                                            placeholder="Chọn chế độ"
                                            noOptionsMessage={() => 'Không tìm thấy'}
                                            styles={{
                                                container: (base) => ({ ...base, width: 200 }),
                                                control: (base) => ({ ...base, minHeight: 31 }),
                                                valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
                                                menu: (base) => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                        {isEmployeeChart && (
                                            <>
                                                <span className="mr-2 font-weight-bold text-nowrap">Lọc theo:</span>
                                                <Select
                                                    className="mr-2"
                                                    value={topFilterOptions.find(option => option.value === topFilter) || topFilterOptions[0]}
                                                    onChange={(option) => setTopFilter(option?.value || 'all')}
                                                    options={topFilterOptions}
                                                    isSearchable
                                                    placeholder="Chọn top"
                                                    noOptionsMessage={() => 'Không tìm thấy'}
                                                    styles={{
                                                        container: (base) => ({ ...base, width: 220 }),
                                                        control: (base) => ({ ...base, minHeight: 31 }),
                                                        valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
                                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                />
                                                <Select
                                                    value={employeePositionOptions.find(option => String(option.value) === String(employeePositionFilter)) || employeePositionOptions[0]}
                                                    onChange={(option) => setEmployeePositionFilter(option?.value || 'all')}
                                                    options={employeePositionOptions}
                                                    isSearchable
                                                    placeholder="Lọc vị trí"
                                                    noOptionsMessage={() => 'Không tìm thấy vị trí'}
                                                    styles={{
                                                        container: (base) => ({ ...base, width: 200 }),
                                                        control: (base) => ({ ...base, minHeight: 31 }),
                                                        valueContainer: (base) => ({ ...base, paddingTop: 0, paddingBottom: 0 }),
                                                        menu: (base) => ({ ...base, zIndex: 9999 })
                                                    }}
                                                />
                                            </>
                                        )}
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {visibleChartData.length === 0 ? (
                                        <div className="text-center py-4 text-muted">Không có dữ liệu hiển thị biểu đồ</div>
                                    ) : (
                                        <Plot
                                            data={barChartData}
                                            layout={barChartLayout}
                                            useResizeHandler={true}
                                            style={{ width: "100%" }}
                                            onClick={(data) => {
                                                if (!isEmployeeChart) {
                                                    return;
                                                }
                                                if (data.points && data.points.length > 0) {
                                                    const pointIndex = data.points[0].pointIndex;
                                                    const clickedMember = visibleChartData[pointIndex];
                                                    if (clickedMember) {
                                                        history.push(`/annual-job/tracking?userId=${clickedMember.id}`);
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Section 3: Ranking Table */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="shadow-sm">
                                <Card.Header className="bg-light">
                                    <h3 className="card-title fw-bold" style={{ fontSize: '16px', margin: 0 }}>
                                        <i className="fas fa-trophy text-warning me-2"></i>
                                        {isAdmin ? 'Bảng xếp hạng KPI nhân viên' : 'KPI cá nhân'}
                                    </h3>
                                </Card.Header>
                                {isAdmin ? (
                                    <Card.Body className="p-0">
                                        <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                            <Table hover className="mb-0 align-middle">
                                                <thead className="bg-light text-muted" style={{ fontSize: '13px' }}>
                                                    <tr>
                                                        <th className="text-center" style={{ width: '60px' }}>STT</th>
                                                        <th>Họ tên</th>
                                                        <th className="text-center">Điểm Thường niên</th>
                                                        <th className="text-center">Điểm Đột xuất</th>
                                                        <th className="text-center">Điểm thực nhận</th>
                                                        <th className="text-center">Chưa đánh giá</th>
                                                        <th className="text-center">KPI dự tính</th>
                                                        <th style={{ width: '220px' }}>Tiến độ thường niên</th>
                                                        <th className="text-center" style={{ width: '150px' }}>Thao tác</th>
                                                    </tr>
                                                </thead>
                                                <tbody style={{ fontSize: '14px' }}>
                                                    {processedData.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="9" className="text-center py-4 text-muted">Không có dữ liệu</td>
                                                        </tr>
                                                    ) : (
                                                        processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((item, index) => {
                                                            const realIndex = (currentPage - 1) * itemsPerPage + index;
                                                            const rawProgress = item.progressPercentage;
                                                            const displayProgress = (rawProgress > 0 && rawProgress < 100) ? rawProgress.toFixed(1) : Math.round(rawProgress);
                                                            const barProgress = Math.min(100, rawProgress);

                                                            return (
                                                                <tr key={item.id}>
                                                                    <td className="text-center font-weight-bold">
                                                                        {realIndex === 0 ? <Badge variant="danger" style={{ fontSize: '12px' }}>🥇 1</Badge> :
                                                                         realIndex === 1 ? <Badge variant="warning" style={{ fontSize: '12px', color: '#fff' }}>🥈 2</Badge> :
                                                                         realIndex === 2 ? <Badge variant="info" style={{ fontSize: '12px' }}>🥉 3</Badge> :
                                                                         (realIndex + 1)}
                                                                    </td>
                                                                    <td>
                                                                        <div className="font-weight-bold text-dark">{item.fullName}</div>
                                                                        <small className="text-muted">{item.userName}</small>
                                                                    </td>
                                                                    <td className="text-center text-success font-weight-bold">
                                                                        {item.totalAnnualScore.toFixed(2)}
                                                                    </td>
                                                                    <td className="text-center text-primary font-weight-bold">
                                                                        {item.totalProjectScore.toFixed(2)}
                                                                    </td>
                                                                    <td className="text-center font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                                                        {item.totalScore.toFixed(2)}
                                                                    </td>
                                                                    <td className="text-center text-muted font-weight-bold">{item.totalUnassessedScore.toFixed(2)}</td>
                                                                    <td className="text-center text-info font-weight-bold">{item.projectedScore.toFixed(2)}</td>
                                                                    <td>
                                                                        {item.hasActiveAnnualJob ? (
                                                                            <div className="d-flex align-items-center">
                                                                                <div style={{ flex: 1, marginRight: 8 }}>
                                                                                    <ProgressBar
                                                                                        variant={displayProgress >= 100 ? "success" : displayProgress >= 50 ? "info" : "warning"}
                                                                                        now={barProgress}
                                                                                        style={{ height: '8px' }}
                                                                                    />
                                                                                </div>
                                                                                <span className="font-weight-bold text-muted" style={{ fontSize: '12px', minWidth: '40px', textAlign: 'right' }}>
                                                                                    {displayProgress}%
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted fst-italic" style={{ fontSize: '12px' }}>Chưa đăng ký phiếu</span>
                                                                        )}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Button
                                                                            variant="outline-info"
                                                                            size="sm"
                                                                            onClick={() => history.push(`/annual-job/tracking?userId=${item.id}`)}
                                                                            style={{ fontSize: '12px', padding: '3px 10px' }}
                                                                        >
                                                                            <i className="fas fa-eye me-1"></i> Xem chi tiết
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                        {processedData.length > itemsPerPage && (
                                            <div className="d-flex justify-content-end p-3 border-top bg-light">
                                                <Pagination
                                                    activePage={currentPage}
                                                    itemsCountPerPage={itemsPerPage}
                                                    totalItemsCount={processedData.length}
                                                    pageRangeDisplayed={5}
                                                    onChange={(pageNumber) => setCurrentPage(pageNumber)}
                                                    itemClass="page-item"
                                                    linkClass="page-link"
                                                    prevPageText="Trước"
                                                    nextPageText="Sau"
                                                    firstPageText="Đầu"
                                                    lastPageText="Cuối"
                                                />
                                            </div>
                                        )}
                                    </Card.Body>
                                ) : (
                                    <Card.Body>
                                        {!personalKpi ? (
                                            <div className="text-center py-4 text-muted">Không có dữ liệu</div>
                                        ) : (
                                            <Row className="align-items-center">
                                                <Col lg={3} md={12} className="mb-3 mb-lg-0">
                                                    <div className="font-weight-bold text-dark" style={{ fontSize: '18px' }}>{personalKpi.fullName}</div>
                                                    <div className="text-muted">{personalKpi.userName}</div>
                                                </Col>
                                                <Col lg={6} md={12}>
                                                    <Row>
                                                        <Col sm={4} className="mb-3 mb-sm-0">
                                                            <div className="text-muted small">Điểm Thường niên</div>
                                                            <div className="text-success font-weight-bold" style={{ fontSize: '24px' }}>
                                                                {personalKpi.totalAnnualScore.toFixed(2)}
                                                            </div>
                                                        </Col>
                                                        <Col sm={4} className="mb-3 mb-sm-0">
                                                            <div className="text-muted small">Điểm Đột xuất</div>
                                                            <div className="text-primary font-weight-bold" style={{ fontSize: '24px' }}>
                                                                {personalKpi.totalProjectScore.toFixed(2)}
                                                            </div>
                                                        </Col>
                                                        <Col sm={4}>
                                                            <div className="text-muted small">Điểm thực nhận</div>
                                                            <div className="font-weight-bold text-dark" style={{ fontSize: '24px' }}>
                                                                {personalKpi.totalScore.toFixed(2)}
                                                            </div>
                                                            <div className="text-muted small mt-1">Chưa đánh giá: {personalKpi.totalUnassessedScore.toFixed(2)}</div>
                                                            <div className="text-info small">KPI dự tính: {personalKpi.projectedScore.toFixed(2)}</div>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                                <Col lg={3} md={12}>
                                                    {(() => {
                                                        const rawProgress = personalKpi.progressPercentage;
                                                        const displayProgress = (rawProgress > 0 && rawProgress < 100) ? rawProgress.toFixed(1) : Math.round(rawProgress);
                                                        const barProgress = Math.min(100, rawProgress);
                                                        return (
                                                            <>
                                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                                    <span className="text-muted small">Tiến độ thường niên</span>
                                                                    <span className="font-weight-bold text-muted">{displayProgress}%</span>
                                                                </div>
                                                                {personalKpi.hasActiveAnnualJob ? (
                                                                    <ProgressBar
                                                                        variant={displayProgress >= 100 ? "success" : displayProgress >= 50 ? "info" : "warning"}
                                                                        now={barProgress}
                                                                        style={{ height: '10px' }}
                                                                    />
                                                                ) : (
                                                                    <span className="text-muted fst-italic" style={{ fontSize: '12px' }}>Chưa đăng ký phiếu</span>
                                                                )}
                                                                <div className="mt-3 text-lg-end">
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        onClick={() => history.push(`/annual-job/tracking?userId=${personalKpi.id}`)}
                                                                    >
                                                                        <i className="fas fa-eye me-1"></i> Xem KPI của tôi
                                                                    </Button>
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </Col>
                                            </Row>
                                        )}
                                    </Card.Body>
                                )}
                            </Card>
                        </Col>
                    </Row>

                    {/* Section 4: Pie Charts */}
                    <Row className="mb-4">
                        <Col md={6} className="mb-3">
                            <Card className="shadow-sm">
                                <Card.Header className="bg-light">
                                    <h3 className="card-title fw-bold" style={{ fontSize: '15px', margin: 0 }}>
                                        <i className="fas fa-chart-pie text-success me-2"></i>
                                        Phân bổ công việc thường niên (KPI)
                                    </h3>
                                </Card.Header>
                                <Card.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    {Object.values(annualStatusCounts).reduce((a, b) => a + b, 0) === 0 ? (
                                        <div className="text-center text-muted">Chưa có công việc thường niên nào</div>
                                    ) : (
                                        <Plot
                                            data={annualPieChartData}
                                            layout={annualPieLayout}
                                            useResizeHandler={true}
                                            style={{ width: "100%" }}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Card className="shadow-sm">
                                <Card.Header className="bg-light">
                                    <h3 className="card-title fw-bold" style={{ fontSize: '15px', margin: 0 }}>
                                        <i className="fas fa-chart-pie text-warning me-2"></i>
                                        Phân bổ dự án đột xuất / khoán
                                    </h3>
                                </Card.Header>
                                <Card.Body className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    {Object.values(projectStatusCounts).reduce((a, b) => a + b, 0) === 0 ? (
                                        <div className="text-center text-muted">Chưa có dự án đột xuất nào</div>
                                    ) : (
                                        <Plot
                                            data={projectPieChartData}
                                            layout={projectPieLayout}
                                            useResizeHandler={true}
                                            style={{ width: "100%" }}
                                        />
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
};

export default Dashboard;
